import { useEffect, useRef } from 'react'
import { useAuthStore } from '../auth/useAuthStore'
import { usePortfolioSummary } from './usePortfolioSummary'
import { recordPortfolioSnapshot } from '../api/portfolioHistory'
import { toastOnce } from '../toast/useToastStore'

const RECORD_INTERVAL_MS = 60_000

/**
 * Persists the user's normalized portfolio value to the backend once per
 * minute. Platform-agnostic — consumes the normalized output of
 * usePortfolioSummary, so any platform whose data layer feeds into that hook
 * is automatically recorded.
 *
 * Mount exactly once at the app shell so recording continues across routes.
 */
export function useRecordPortfolioSnapshot(): void {
  const accountId = useAuthStore((s) => s.accountId)
  const summary = usePortfolioSummary()
  const summaryRef = useRef(summary)
  summaryRef.current = summary

  useEffect(() => {
    if (!accountId) return

    const tick = () => {
      const s = summaryRef.current
      if (!s.data || !Number.isFinite(s.portfolioValue) || s.portfolioValue <= 0) return
      recordPortfolioSnapshot({
        accountId,
        portfolioValue: s.portfolioValue,
        positionsValue: s.positionsValue,
        cashValue: s.cashValue,
        dayChange: s.dayChange,
      }).catch(() => {
        // Fire-and-forget, but say so once — silent failure here means
        // gaps in the portfolio value chart.
        toastOnce.error(
          'portfolio-snapshot-failing',
          'Portfolio snapshot recording is failing — the portfolio chart may have gaps.',
        )
      })
    }

    tick()
    const id = window.setInterval(tick, RECORD_INTERVAL_MS)
    return () => window.clearInterval(id)
  }, [accountId])
}
